import { Link } from 'react-router-dom'
import type { ChangeEvent, ChangeEventKind } from '@/shared/api/types'
import {
  eventCallHref,
  eventPerson,
  eventTitle,
  formatEventValue,
} from './presentation'
import styles from './ClientEventRow.module.css'

interface ClientEventRowProps {
  event: ChangeEvent
  compact?: boolean
}

/** Одна строка ленты — одинаковая на обзоре и на полной странице. */
export function ClientEventRow({ event, compact = false }: ClientEventRowProps) {
  const person = eventPerson(event)
  const before = formatEventValue(event, event.before)
  const after = formatEventValue(event, event.after)
  const href = eventCallHref(event)
  const showBefore =
    event.before.trim().length > 0 || event.kind === 'label' || event.field === 'label'
  const showPhone =
    event.displayName.trim().length > 0 && event.displayName.trim() !== event.phoneFormatted.trim()

  return (
    <article
      className={[
        styles.row,
        compact ? styles.compact : '',
        event.important ? styles.importantRow : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className={`${styles.icon} ${toneClass(event)}`} aria-hidden="true">
        <EventIcon kind={event.kind} />
      </span>

      <div className={styles.body}>
        <div className={styles.heading}>
          <strong className={styles.person}>{person}</strong>
          <span className={styles.subject}>{eventTitle(event)}</span>
          {event.important && <span className={styles.important}>Важное</span>}
        </div>

        <div className={styles.change}>
          {showBefore && before !== after && (
            <>
              <span className={styles.before}>{before}</span>
              <span className={styles.arrow} aria-hidden="true">
                →
              </span>
            </>
          )}
          <span className={styles.after}>{after}</span>
        </div>

        <div className={styles.meta}>
          {showPhone && <span>{event.phoneFormatted}</span>}
          <span>{event.author === 'agent' ? 'Агент' : event.authorName.trim() || 'Вы'}</span>
          <time dateTime={event.at}>{formatEventTime(event.at)}</time>
        </div>
      </div>

      {href && (
        <Link className={styles.open} to={href} aria-label={`Открыть звонок с ${person}`}>
          <span className={styles.openLabel}>К звонку</span>
          <ArrowIcon />
        </Link>
      )}
    </article>
  )
}

function toneClass(event: ChangeEvent): string {
  if (
    event.kind === 'do_not_call' ||
    event.after === 'negative' ||
    event.after === 'do_not_call'
  ) {
    return styles.iconDanger ?? ''
  }
  if (event.after === 'thinking' || event.kind === 'status') return styles.iconWarning ?? ''
  if (event.after === 'cold' || event.kind === 'decision_maker') return styles.iconInfo ?? ''
  if (event.kind === 'meeting' || event.kind === 'label') return styles.iconPositive ?? ''
  return styles.iconNeutral ?? ''
}

function formatEventTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'Время не указано'

  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })

  if (sameDay(date, now)) return `Сегодня, ${time}`
  if (sameDay(date, yesterday)) return `Вчера, ${time}`

  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function sameDay(left: Date, right: Date): boolean {
  return (
    left.getDate() === right.getDate() &&
    left.getMonth() === right.getMonth() &&
    left.getFullYear() === right.getFullYear()
  )
}

function EventIcon({ kind }: { kind: ChangeEventKind }) {
  if (kind === 'meeting') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="2.2" y="3.4" width="11.6" height="10.2" rx="2" stroke="currentColor" strokeWidth="1.4" />
        <path d="M5 2V4.4M11 2V4.4M2.2 6.4H13.8M5.2 9L7.1 10.8L10.9 7.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }

  if (kind === 'decision_maker') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5.2" r="2.4" stroke="currentColor" strokeWidth="1.4" />
        <path d="M3.3 13.5C3.7 10.7 5.3 9.3 8 9.3C10.7 9.3 12.3 10.7 12.7 13.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    )
  }

  if (kind === 'do_not_call') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="5.7" stroke="currentColor" strokeWidth="1.4" />
        <path d="M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )
  }

  if (kind === 'label') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M2.5 3.2H8.5L13.5 8.1L8.5 13H2.5V3.2Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        <circle cx="5.2" cy="6" r="1" fill="currentColor" />
      </svg>
    )
  }

  if (kind === 'status') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M3 4H13M3 8H10M3 12H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )
  }

  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 2.2V13.8M2.2 8H13.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="8" r="5.7" stroke="currentColor" strokeWidth="1.2" opacity="0.45" />
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M3 7H11M8 4L11 7L8 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
